import { useState } from 'react';
import { MessageSquare, Pencil, Trash2 } from 'lucide-react';
import { Button, Textarea, Badge, Checkbox, IconButton, Card, CardBody } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { formatDateTime } from '@/lib/format';
import {
  useTicketComments,
  useCreateTicketComment,
  useUpdateTicketComment,
  useDeleteTicketComment,
  type TicketCommentPayload,
} from '@/api/tickets';
import { useMe } from '@/api/me';

interface ConversationTabProps {
  ticketId: number;
  /**
   * The ticket's opening request, rendered pinned as the first message of the
   * thread (industry pattern: the description is the first message, not a lone
   * strip). Omit to show only comments.
   */
  originalRequest?: { content: string; author: string; at: string };
}

/**
 * Comments list + composer. Supports create, inline edit, and delete of own
 * comments. Edit/delete path uses the singular `/tickets/comment/:id` endpoint.
 * When `originalRequest` is given it is pinned as the first thread entry.
 */
export function ConversationTab({ ticketId, originalRequest }: ConversationTabProps) {
  const toast = useToast();
  const { data: me } = useMe();
  const myEmployeeId = me?.employee?.employee_id ?? -1;

  const { data, isLoading, isError, refetch } = useTicketComments(ticketId);
  const comments = data?.results ?? [];

  const createMutation = useCreateTicketComment(ticketId);
  const updateMutation = useUpdateTicketComment();
  const deleteMutation = useDeleteTicketComment();

  const [newText, setNewText] = useState('');
  const [sendToCustomer, setSendToCustomer] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [editId, setEditId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [editError, setEditError] = useState(false);

  const [deleteId, setDeleteId] = useState<number | null>(null);

  const handleCreate = async () => {
    const trimmed = newText.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await createMutation.mutateAsync({
        ticket_id: ticketId,
        ticket_comment_text: trimmed,
        ticket_comment_pinted: 'f',
        ticket_comment_visibility: sendToCustomer ? 'public' : 'internal',
        employee_id: myEmployeeId,
        employee_name: me?.employee?.employee_name ?? '',
        created_at: '',
        updated_at: '',
        ticket_comment_id: 0,
      } as TicketCommentPayload);
      setNewText('');
      setSendToCustomer(false);
      toast.success('Comment added');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong.';
      toast.error('Failed to add comment', { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (c: TicketCommentPayload) => {
    setEditId(c.ticket_comment_id);
    setEditText(c.ticket_comment_text);
    setEditError(false);
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditText('');
    setEditError(false);
  };

  const handleUpdate = async () => {
    const trimmed = editText.trim();
    if (!trimmed) { setEditError(true); return; }
    if (editId === null) return;
    try {
      await updateMutation.mutateAsync({
        ticket_comment_id: editId,
        ticket_comment_text: trimmed,
        ticket_id: ticketId,
      });
      cancelEdit();
      toast.success('Comment updated');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong.';
      toast.error('Failed to update comment', { description: msg });
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await deleteMutation.mutateAsync({ ticket_comment_id: deleteId, ticket_id: ticketId });
      setDeleteId(null);
      toast.success('Comment deleted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong.';
      toast.error('Failed to delete comment', { description: msg });
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-2)' }}>
        {[1, 2, 3].map((i) => <Skeleton key={i} style={{ height: 56 }} />)}
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load comments"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>Retry</Button>
        }
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <MessageSquare size={16} style={{ color: 'var(--vn-fg-muted)' }} />
        <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-medium)' }}>
          Conversation
        </span>
        {comments.length > 0 && (
          <Badge size="sm" tone="neutral">
            {comments.length}
          </Badge>
        )}
      </div>

      {/* Thread: pinned original request (if any) + comments */}
      <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        {originalRequest && (
          <Card flat style={{ background: 'var(--vn-bg-subtle)' }}>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 'var(--vn-space-2)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-medium)' }}>
                    {originalRequest.author || 'Reporter'}
                  </span>
                  <Badge size="sm" tone="neutral">Original request</Badge>
                </div>
                <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', flexShrink: 0 }}>
                  {formatDateTime(originalRequest.at)}
                </span>
              </div>
              <p
                style={{
                  fontSize: 'var(--vn-font-size-sm)',
                  color: 'var(--vn-fg-default)',
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                }}
              >
                {originalRequest.content}
              </p>
            </CardBody>
          </Card>
        )}

        {comments.length === 0 ? (
          <p style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            No comments yet. Add the first one below.
          </p>
        ) : (
          comments.map((c) => {
            const isOwn = c.employee_id === myEmployeeId;
            const isEditing = editId === c.ticket_comment_id;

            return (
              <Card
                key={c.ticket_comment_id}
                flat
                style={{
                  background: c.ticket_comment_visibility === 'public'
                    ? 'var(--vn-info-subtle)'
                    : undefined,
                }}
              >
                <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                  {/* Meta row */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 'var(--vn-space-2)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-medium)' }}>
                        {c.employee_name || 'Unknown'}
                      </span>
                      {c.ticket_comment_visibility === 'public' && (
                        <Badge size="sm" tone="info" appearance="outline">
                          Public
                        </Badge>
                      )}
                      {c.ticket_comment_visibility !== 'public' && (
                        <span
                          style={{
                            fontSize: 'var(--vn-font-size-xs)',
                            color: 'var(--vn-fg-muted)',
                          }}
                        >
                          Internal note
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-1)', flexShrink: 0 }}>
                      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                        {formatDateTime(c.created_at)}
                      </span>
                      {isOwn && !isEditing && (
                        <>
                          <IconButton
                            size="sm"
                            variant="ghost"
                            aria-label="Edit comment"
                            onClick={() => startEdit(c)}
                          >
                            <Pencil size={14} />
                          </IconButton>
                          <IconButton
                            size="sm"
                            variant="ghost"
                            aria-label="Delete comment"
                            onClick={() => setDeleteId(c.ticket_comment_id)}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Body or edit form */}
                  {isEditing ? (
                    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                      <Field
                        label=""
                        error={editError ? 'Comment cannot be empty.' : undefined}
                      >
                        {(p) => (
                          <Textarea
                            {...p}
                            rows={3}
                            value={editText}
                            onChange={(e) => {
                              setEditText(e.target.value);
                              if (editError) setEditError(false);
                            }}
                          />
                        )}
                      </Field>
                      <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={handleUpdate}
                          disabled={updateMutation.isPending}
                        >
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={cancelEdit}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p
                      style={{
                        fontSize: 'var(--vn-font-size-sm)',
                        color: 'var(--vn-fg-default)',
                        whiteSpace: 'pre-wrap',
                        margin: 0,
                      }}
                    >
                      {c.ticket_comment_text}
                    </p>
                  )}
                </CardBody>
              </Card>
            );
          })
        )}
      </div>

      {/* Composer */}
      <Card flat style={{ background: 'var(--vn-bg-subtle)' }}>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <Textarea
            rows={3}
            value={newText}
            placeholder="Write a comment…"
            onChange={(e) => setNewText(e.target.value)}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--vn-space-3)',
              flexWrap: 'wrap',
            }}
          >
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--vn-space-2)',
                fontSize: 'var(--vn-font-size-sm)',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <Checkbox
                checked={sendToCustomer}
                disabled={submitting}
                onCheckedChange={(v) => setSendToCustomer(v === true)}
              />
              <span>
                Send to customer
                <span style={{ color: 'var(--vn-fg-muted)' }}>
                  {' '}— visible in Partner Portal (default: internal note)
                </span>
              </span>
            </label>
            <Button
              size="sm"
              variant="primary"
              onClick={handleCreate}
              disabled={submitting || !newText.trim()}
            >
              {submitting ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteId !== null}
        onOpenChange={(o) => { if (!o) setDeleteId(null); }}
        title="Delete comment"
        description="Are you sure you want to delete this comment? This cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
